import React from 'react';
import { FEED_LINK } from './investigation/feedCardStyles';
import InspectDrawerShell from './InspectDrawerShell';

function InspectUrlLink({ label, url, testId }) {
  const href = String(url || '').trim();
  if (!href.startsWith('http')) return null;

  return (
    <div
      className="rounded-xl border border-cyan-500/35 bg-cyan-950/35 p-3 ring-1 ring-cyan-400/15"
      data-testid={testId}
    >
      <p className="text-[9px] font-black uppercase tracking-widest text-cyan-300/90">
        {label}
      </p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-1.5 block break-all font-mono text-[11px] font-semibold ${FEED_LINK}`}
      >
        {href}
      </a>
    </div>
  );
}

/**
 * GHunt Maps / Calendar inspector — right slide-over drawer (Email Search).
 *
 * @param {{ isOpen: boolean, onClose: Function, mode?: 'maps'|'calendar', data?: object }} props
 */
export default function GhuntInspectModal({ isOpen, onClose, mode = 'maps', data }) {
  if (!isOpen || !data) return null;

  const details = data.details && typeof data.details === 'object' ? data.details : {};
  const mapsInspect = details.maps_inspect && typeof details.maps_inspect === 'object'
    ? details.maps_inspect
    : {};
  const calendarInspect = details.calendar_inspect && typeof details.calendar_inspect === 'object'
    ? details.calendar_inspect
    : {};
  const gaia = data.gaia_id || details.gaia_id || mapsInspect.gaia_id || '';
  const email = data.target || data.email || calendarInspect.email || '';
  const isMaps = mode === 'maps';

  const stats = mapsInspect.stats && typeof mapsInspect.stats === 'object' ? mapsInspect.stats : {};
  const reviews = Array.isArray(mapsInspect.recent_reviews) ? mapsInspect.recent_reviews : [];
  const events = Array.isArray(calendarInspect.events) ? calendarInspect.events : [];
  const statEntries = Object.entries(stats).filter(([, v]) => v != null && v !== '');
  const mapsUrl =
    mapsInspect.contributor_url ||
    data.maps_url ||
    details.maps_url ||
    (gaia ? `https://www.google.com/maps/contrib/${gaia}` : '');
  const calendarEmbedUrl =
    calendarInspect.embed_url ||
    data.calendar_url ||
    details.calendar_url ||
    '';
  const calendarIcsUrl = calendarInspect.ics_url || calendarInspect.download_ics || '';
  const sourceHref = isMaps ? mapsUrl : (calendarEmbedUrl || calendarIcsUrl);

  return (
    <InspectDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={isMaps ? 'Google Maps inspection' : 'Google Calendar inspection'}
      testId={`ghunt-inspect-modal-${mode}`}
      eyebrow={`GHunt · ${isMaps ? 'Google Maps' : 'Google Calendar'}`}
      title={isMaps ? 'Maps Contributor Profile' : 'Public Calendar Events'}
      sourceHref={sourceHref}
      subtitle={
        <>
          {gaia ? (
            <p className="mt-1 font-mono text-[11px] text-slate-400">Gaia ID: {gaia}</p>
          ) : null}
          {!isMaps && email ? (
            <p className="mt-0.5 font-mono text-[10px] text-slate-500">{email}</p>
          ) : null}
        </>
      }
    >
      {isMaps ? (
        <>
          {statEntries.length > 0 ? (
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              {statEntries.map(([key, value]) => (
                <div
                  key={key}
                  className="rounded-xl border border-white/10 bg-slate-900/60 p-3"
                >
                  <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                    {key.replace(/_/g, ' ')}
                  </dt>
                  <dd className="mt-1 text-slate-200">{String(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-slate-400">
              No Maps statistics were returned for this Gaia ID. The contributor profile may still
              exist on Google Maps.
            </p>
          )}

          {reviews.length > 0 ? (
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
              <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
                Recent Reviews ({reviews.length})
              </p>
              <ul className="m-0 max-h-48 list-none space-y-1 overflow-y-auto p-0 text-sm text-slate-300">
                {reviews.map((review, idx) => (
                  <li key={`${review}-${idx}`} className="border-b border-white/5 py-1 last:border-0">
                    {review}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {mapsUrl ? (
            <InspectUrlLink
              label="Public Maps profile"
              url={mapsUrl}
              testId="ghunt-inspect-maps-url"
            />
          ) : null}
        </>
      ) : (
        <>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
              <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                Total Events
              </dt>
              <dd className="mt-1 font-bold text-slate-200">
                {calendarInspect.total_events ?? events.length ?? 0}
              </dd>
            </div>
            <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
              <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                Public Calendar
              </dt>
              <dd className="mt-1 text-slate-200">
                {calendarInspect.has_public_calendar ? 'Detected' : 'Not enumerated'}
              </dd>
            </div>
          </dl>

          {calendarInspect.note ? (
            <p className="text-sm text-slate-400">{calendarInspect.note}</p>
          ) : null}

          {events.length > 0 ? (
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
              <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
                Events
              </p>
              <ul className="m-0 max-h-56 list-none space-y-2 overflow-y-auto p-0 text-sm text-slate-300">
                {events.map((ev, idx) => {
                  const title = ev?.titlu || ev?.title || ev?.name || 'Untitled';
                  const when = ev?.data || ev?.start || ev?.date || '';
                  return (
                    <li key={`${title}-${idx}`} className="rounded-lg border border-white/5 px-3 py-2">
                      <span className="font-medium text-slate-100">{title}</span>
                      {when ? (
                        <span className="mt-0.5 block font-mono text-[10px] text-slate-500">
                          {when}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              No public calendar events were extracted during the scan.
            </p>
          )}

          {calendarEmbedUrl ? (
            <InspectUrlLink
              label="Public calendar embed"
              url={calendarEmbedUrl}
              testId="ghunt-inspect-calendar-embed-url"
            />
          ) : null}
          {calendarIcsUrl ? (
            <InspectUrlLink
              label="ICS feed"
              url={calendarIcsUrl}
              testId="ghunt-inspect-calendar-ics-url"
            />
          ) : null}
        </>
      )}
    </InspectDrawerShell>
  );
}
